import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";

import { CompaniesService } from "../../../services/companies.service";
import {
  CompaniesEnabled,
  EnabledCompaniesSnapshot,
  EnabledProductKey,
  Way2VatStorage,
} from "./enabled-companies.types";

@Injectable({
  providedIn: "root",
})
export class EnabledCompaniesStoreService {
  private readonly storageKeyPrefix = "enabled_companies_snapshot_";
  private snapshotRequest$: Observable<EnabledCompaniesSnapshot> | null = null;
  private snapshotRequestKey: string | null = null;

  constructor(private companiesService: CompaniesService) {}

  fetchCompaniesEnabled(product: EnabledProductKey) {
    const way2vatData = this.getWay2VatData();
    if (!way2vatData) {
      return;
    }

    const snapshot$ = this.fetchSnapshot(way2vatData);
    if (!snapshot$) {
      return;
    }

    return snapshot$.pipe(
      map((snapshot) =>
        this.mapSnapshotToCompaniesEnabled(snapshot, product, way2vatData),
      ),
    );
  }

  getStoredCompaniesEnabled(product: EnabledProductKey) {
    const way2vatData = this.getWay2VatData();
    if (!way2vatData) {
      return null;
    }

    const snapshot = this.getStoredSnapshot(way2vatData.payload.userId);
    if (!snapshot) {
      return null;
    }

    return this.mapSnapshotToCompaniesEnabled(snapshot, product, way2vatData);
  }

  private fetchSnapshot(way2vatData: Way2VatStorage) {
    const requestKey = this.getSnapshotRequestKey(way2vatData);
    if (this.snapshotRequest$ && this.snapshotRequestKey === requestKey) {
      return this.snapshotRequest$;
    }

    const companyIds = this.isAdmin(way2vatData.payload.role)
      ? []
      : way2vatData.payload.companiesIds || [];

    const request$ = this.companiesService.companiesWithProducts(companyIds).pipe(
      map((companies) => ({
        userId: Number(way2vatData.payload.userId),
        userRole: way2vatData.payload.role,
        companies,
      })),
      tap((snapshot) => {
        localStorage.setItem(
          this.getSnapshotStorageKey(way2vatData.payload.userId),
          JSON.stringify(snapshot),
        );
      }),
      catchError((error) => {
        this.snapshotRequest$ = null;
        this.snapshotRequestKey = null;
        return throwError(() => error);
      }),
      shareReplay(1),
    );

    this.snapshotRequest$ = request$;
    this.snapshotRequestKey = requestKey;

    return request$;
  }

  private getStoredSnapshot(userId: string): EnabledCompaniesSnapshot | null {
    const entry = localStorage.getItem(this.getSnapshotStorageKey(userId));
    if (!entry) {
      return null;
    }

    try {
      return JSON.parse(entry);
    } catch (error) {
      console.error("Error parsing stored enabled companies data");
      localStorage.removeItem(this.getSnapshotStorageKey(userId));
      return null;
    }
  }

  private mapSnapshotToCompaniesEnabled(
    snapshot: EnabledCompaniesSnapshot,
    product: EnabledProductKey,
    way2vatData: Way2VatStorage,
  ): CompaniesEnabled {
    const productCompanyIds = this.getProductCompanyIds(way2vatData, product);

    return {
      userId: snapshot.userId,
      userRole: snapshot.userRole,
      companies: snapshot.companies
        .filter(
          (company) =>
            company.enabledProducts.includes(product) &&
            (!productCompanyIds || productCompanyIds.has(company.id)),
        )
        .map((company) => ({
          id: company.id,
          name: company.name,
          accountName: company.accountName,
          enabled: true,
        })),
    };
  }

  private getSnapshotStorageKey(userId: string) {
    return `${this.storageKeyPrefix}${userId}`;
  }

  private getSnapshotRequestKey(way2vatData: Way2VatStorage) {
    return JSON.stringify({
      userId: way2vatData.payload.userId,
      role: way2vatData.payload.role,
      companiesIds: way2vatData.payload.companiesIds || [],
    });
  }

  private getProductCompanyIds(
    way2vatData: Way2VatStorage,
    product: EnabledProductKey,
  ) {
    if (this.isAdmin(way2vatData.payload.role)) {
      return null;
    }

    const enabledIndices = way2vatData.payload.enabledProducts?.[product] || [];
    const companyIds = way2vatData.payload.companiesIds || [];

    return new Set(
      enabledIndices
        .map((index) => companyIds[index])
        .filter((companyId): companyId is string => Boolean(companyId)),
    );
  }

  private isAdmin(role: string) {
    return role?.toLowerCase() === "admin";
  }

  private getWay2VatData(): Way2VatStorage | null {
    try {
      const data = localStorage.getItem("way2vat");
      if (!data) return null;

      const parsedData = JSON.parse(data);
      const access_token = localStorage.getItem("access_token");

      const token = access_token || parsedData.access_token;
      const jwtToken = JSON.parse(atob(token.split(".")[1]));

      const expires = jwtToken.exp * 1000;
      const currTime = Date.now();
      const isExpired = currTime >= expires;

      if (isExpired) return null;
      return parsedData;
    } catch (error) {
      console.error("Error parsing way2vat data from local storage:", error);
      return null;
    }
  }
}
